import { useEffect, useState } from "react";
import axios from "axios";
import { motion } from "framer-motion";
import { MapPin, Building2, Info } from "lucide-react";
import { API } from "../App";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "./ui/select";

/**
 * Barbados-only home preview: optional inland delivery suggestion.
 * Customer confirms parish, TAMIS, and carrier at checkout.
 */
export default function BarbadosDeliveryPreview() {
  const [parishFees, setParishFees] = useState(null);
  const [selectedParish, setSelectedParish] = useState("");
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    axios
      .get(`${API}/shipping/parish-fees`, { withCredentials: true })
      .then((res) => {
        if (cancelled) return;
        setParishFees(res.data);
        const parishes = res.data?.parishes || [];
        if (parishes.length) {
          setSelectedParish(parishes[0].parish);
        }
      })
      .catch(() => {
        if (!cancelled) setParishFees(null);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const parishRow = parishFees?.parishes?.find((p) => p.parish === selectedParish);
  const deliveryFee = parishRow?.fee ?? null;

  if (loading) {
    return (
      <section className="py-16 bg-gradient-to-b from-orange-50/80 to-white border-y border-orange-100/80">
        <div className="max-w-3xl mx-auto px-4 text-center text-sm text-slate-500">
          Loading Barbados delivery preview…
        </div>
      </section>
    );
  }

  if (!parishFees?.parishes?.length) return null;

  return (
    <section
      className="py-16 md:py-20 bg-gradient-to-b from-orange-50/80 to-white border-y border-orange-100/80"
      data-testid="barbados-delivery-preview"
    >
      <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
        <motion.div
          initial={{ opacity: 0, y: 16 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="text-center mb-8"
        >
          <span className="inline-flex items-center gap-1.5 bg-orange-100 text-orange-800 text-xs font-semibold px-3 py-1 rounded-full mb-4">
            🇧🇧 Barbados
          </span>
          <h2 className="font-display text-2xl sm:text-3xl font-extrabold text-slate-900 mb-2 tracking-tight">
            Local delivery preview
          </h2>
          <p className="text-slate-600 text-sm sm:text-base max-w-lg mx-auto">
            After international shipping to our Culloden Road HQ, you choose how to receive
            your package. This is a preview only — you confirm at checkout.
          </p>
        </motion.div>

        <div className="mb-6">
          <label
            htmlFor="bb-parish-preview"
            className="block text-xs font-medium text-slate-500 uppercase tracking-wider mb-2 text-center"
          >
            Your parish (for estimate)
          </label>
          <Select value={selectedParish} onValueChange={setSelectedParish}>
            <SelectTrigger
              id="bb-parish-preview"
              className="max-w-sm mx-auto bg-white"
              data-testid="bb-parish-select"
            >
              <SelectValue placeholder="Select parish" />
            </SelectTrigger>
            <SelectContent>
              {parishFees.parishes.map((p) => (
                <SelectItem key={p.parish} value={p.parish}>
                  {p.parish}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        <div className="grid sm:grid-cols-2 gap-4">
          <motion.div
            key={`collect-${selectedParish}`}
            initial={{ opacity: 0, scale: 0.98 }}
            animate={{ opacity: 1, scale: 1 }}
            className="rounded-2xl border-2 border-emerald-200 bg-white p-5 shadow-sm"
            data-testid="bb-option-collect"
          >
            <div className="flex items-start gap-3">
              <div className="p-2 rounded-xl bg-emerald-100">
                <Building2 className="h-5 w-5 text-emerald-600" />
              </div>
              <div className="flex-1 min-w-0">
                <p className="font-bold text-slate-900">Collect at HQ</p>
                <p className="text-xs text-slate-500 mt-1">
                  {parishFees.hub?.address || "Culloden Road, St. Michael"} (
                  {parishFees.hub?.postal || "BB14018"})
                </p>
                <p className="text-lg font-extrabold text-emerald-600 mt-3">FREE</p>
              </div>
            </div>
          </motion.div>

          <motion.div
            key={`deliver-${selectedParish}`}
            initial={{ opacity: 0, scale: 0.98 }}
            animate={{ opacity: 1, scale: 1 }}
            className="rounded-2xl border-2 border-orange-200 bg-white p-5 shadow-sm"
            data-testid="bb-option-deliver"
          >
            <div className="flex items-start gap-3">
              <div className="p-2 rounded-xl bg-orange-100">
                <MapPin className="h-5 w-5 text-orange-600" />
              </div>
              <div className="flex-1 min-w-0">
                <p className="font-bold text-slate-900">Deliver to you</p>
                <p className="text-xs text-slate-500 mt-1">
                  Private courier from HQ to {selectedParish || "your parish"}
                </p>
                <p className="text-lg font-extrabold text-orange-600 mt-3">
                  {deliveryFee != null ? `~$${deliveryFee.toFixed(2)} USD` : "—"}
                  <span className="text-xs font-normal text-slate-400 ml-1">est.</span>
                </p>
              </div>
            </div>
          </motion.div>
        </div>

        <p className="flex items-start justify-center gap-2 text-xs text-slate-500 mt-6 text-center max-w-md mx-auto">
          <Info className="h-4 w-4 shrink-0 mt-0.5" />
          <span>
            International FedEx/DHL rates, TAMIS ID, and your final delivery choice are set at
            checkout.
          </span>
        </p>
      </div>
    </section>
  );
}
